// SPDX-License-Identifier: AGPL-3.0-or-later

// Profile badges drawn inline (bundled, no network round-trip, crisp at any size). Flat facets in a few tones,
// no outline, glow or shadow - the clean look of Discord's own badges.

import type React from 'react';

interface BadgeIconProps {
	size?: number;
	className?: string;
	style?: React.CSSProperties;
}

/** First 5000 members: a flat golden hexagon crest with a cream four-point star. */
export function FoundingMemberBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" className={className} style={style} aria-hidden>
			<path d="M12 2 20.7 7v10L12 22Z" fill="#ff9f1c" />
			<path d="M12 2 3.3 7v10L12 22Z" fill="#ffbe3d" />
			<path d="M12 2 3.3 7 12 12Z" fill="#ffd26b" />
			<path d="M12 2l8.7 5L12 12Z" fill="#ffb12e" />
			<path
				d="M12 6.6c.5 3 2.4 4.9 5.4 5.4-3 .5-4.9 2.4-5.4 5.4-.5-3-2.4-4.9-5.4-5.4 3-.5 4.9-2.4 5.4-5.4Z"
				fill="#fff6dd"
			/>
		</svg>
	);
}

/** Server boost: a flat faceted pink gem with a small sparkle. */
export function BoostGemBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" className={className} style={style} aria-hidden>
			<path d="M6 4h4l-1.6 5H2Z" fill="#ff9cfb" />
			<path d="M10 4h4l1.6 5H8.4Z" fill="#ffc4fd" />
			<path d="M14 4h4l4 5h-6.4Z" fill="#ff84fa" />
			<path d="M2 9h6.4L12 21Z" fill="#f25de9" />
			<path d="M8.4 9h7.2L12 21Z" fill="#ff78f8" />
			<path d="M15.6 9H22L12 21Z" fill="#d94bd3" />
			<path
				d="M19.6 1.4c.2 1.05.65 1.5 1.7 1.7-1.05.2-1.5.65-1.7 1.7-.2-1.05-.65-1.5-1.7-1.7 1.05-.2 1.5-.65 1.7-1.7Z"
				fill="#fff"
			/>
		</svg>
	);
}

/** Lafon Pro: a two-tone blurple crown with pearl tips and a small diamond on the band. */
export function ProBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" className={className} style={style} aria-hidden>
			<path d="M3.6 7.6 8.4 11.6 12 4.4 12 16.6H5.4Z" fill="#9c8fff" />
			<path d="M20.4 7.6 15.6 11.6 12 4.4 12 16.6h6.6Z" fill="#7563f4" />
			<rect x="5" y="16.4" width="14" height="3.4" rx="1" fill="#5b4ad8" />
			<rect x="5" y="16.4" width="7" height="3.4" rx="1" fill="#6c5be6" />
			<circle cx="3.6" cy="7.4" r="1.5" fill="#e6e1ff" />
			<circle cx="12" cy="4.2" r="1.6" fill="#ffffff" />
			<circle cx="20.4" cy="7.4" r="1.5" fill="#e6e1ff" />
			<path d="M12 15.9l1.3-1.6-1.3-1.6-1.3 1.6Z" fill="#ffffff" />
		</svg>
	);
}

/** Lafon Prime (lifetime): a crowned golden medallion with soft champagne wings and an amethyst star. */
export function PrimeBadgeIcon({size = 20, className, style}: BadgeIconProps) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" className={className} style={style} aria-hidden>
			<path
				d="M8.6 14.2C7.4 8.6 3.9 6.4.8 6.9c1.4.7 2.4 1.7 3 2.8-1.5 0-2.7.4-3.4 1.2 1.8.3 3.1 1 4 2-1.4.3-2.4.9-3 1.8 1.8.7 3.6.8 5.2.3-.9.6-1.5 1.5-1.7 2.4 2.5.1 4.6-1 5.7-2.6Z"
				fill="#f0d4a0"
			/>
			<path
				d="M15.4 14.2c1.2-5.6 4.7-7.8 7.8-7.3-1.4.7-2.4 1.7-3 2.8 1.5 0 2.7.4 3.4 1.2-1.8.3-3.1 1-4 2 1.4.3 2.4.9 3 1.8-1.8.7-3.6.8-5.2.3.9.6 1.5 1.5 1.7 2.4-2.5.1-4.6-1-5.7-2.6Z"
				fill="#dcb574"
			/>
			<circle cx="12" cy="14.4" r="5.4" fill="#e0b665" />
			<path d="M12 9a5.4 5.4 0 0 0 0 10.8Z" fill="#f0ce8a" />
			<path
				d="M12 10.9c.36 2 1.46 3.1 3.5 3.5-2 .36-3.14 1.46-3.5 3.5-.36-2-1.46-3.14-3.5-3.5 2-.4 3.14-1.5 3.5-3.5Z"
				fill="#7d6bb5"
			/>
			<path d="M8.5 9.2 8.8 5.2l1.9 1.8L12 4.4V9.2Z" fill="#f0ce8a" />
			<path d="M15.5 9.2 15.2 5.2l-1.9 1.8L12 4.4V9.2Z" fill="#ddb06a" />
		</svg>
	);
}
